// ScheduledCallsPage.jsx — Fully Responsive, Matches LeadManagement UI

import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Form,
  Badge,
  Card,
  Table,
  Row,
  Col,
} from "react-bootstrap";

import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import { toast } from "react-toastify";
import { batchCallsAPI, leadsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/LeadManagement.css";

const ScheduledCallsPage = () => {
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [leadDetails, setLeadDetails] = useState({});
  const [leadLoading, setLeadLoading] = useState(null);

  /* Load All Scheduled Calls */
  useEffect(() => {
    loadScheduledCalls();
  }, []);

  const loadScheduledCalls = async () => {
    try {
      const res = await batchCallsAPI.getScheduledCalls();
      setScheduledCalls(res?.data || []);
    } catch {
      toast.error("Failed to load scheduled calls.");
    } finally {
      setLoading(false);
    }
  };

  /* Fetch Full Lead Details When Clicking Row */
  const loadLeadDetails = async (leadId) => {
    try {
      setLeadLoading(leadId);
      const res = await leadsAPI.getLead(leadId);
      setLeadDetails((prev) => ({ ...prev, [leadId]: res.data }));
    } catch {
      toast.error("Failed to load lead details.");
    } finally {
      setLeadLoading(null);
    }
  };

  const toggleExpandRow = async (call) => {
    const id = call.id;
    const leadId = call.lead.id;

    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    if (!leadDetails[leadId]) {
      await loadLeadDetails(leadId);
    }

    setExpandedId(id);
  };

  /* Cancel Scheduled Call */
  const handleCancel = async (call) => {
    if (!window.confirm("Cancel this scheduled call?")) return;

    try {
      setActionLoading(call.id);
      await batchCallsAPI.cancelBatchCall(call.batch_id);
      await batchCallsAPI.deleteScheduledCall(call.id);
      
      toast.success("Scheduled call cancelled.");
      loadScheduledCalls();
    } catch {
      toast.error("Failed to cancel call.");
    } finally {
      setActionLoading(null);
    }
  };

  /* Countdown Helper */
  const getCountdown = (scheduledTime) => {
    const diff = new Date(scheduledTime).getTime() - Date.now();
    if (diff <= 0) return { label: "Overdue", variant: "danger" };

    const m = Math.floor(diff / 60000);
    if (m < 5) return { label: "Due Now", variant: "danger" };
    if (m < 30) return { label: `${m}m`, variant: "warning" };

    const h = Math.floor(m / 60);
    if (h < 24) return { label: `${h}h`, variant: "info" };

    return { label: `${Math.floor(h / 24)}d`, variant: "info" };
  };

  /* Search Filtering */
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return scheduledCalls.filter(({ lead }) =>
      lead.name.toLowerCase().includes(q) ||
      lead.phone.includes(q) ||
      (lead.email || "").toLowerCase().includes(q)
    );
  }, [scheduledCalls, searchTerm]);

  /* Expanded Card Renderer (Shared by Desktop & Mobile) */
  const renderExpandedDetails = (call) => {
    const leadId = call.lead.id;
    const details = leadDetails[leadId];

    return (
      <Card className="shadow-sm my-2">
        <Card.Body>
          {leadLoading === leadId && (
            <div className="text-center py-3">
              <LoadingSpinner text="Loading lead details..." />
            </div>
          )}

          {details && (
            <>
              <h5 className="fw-bold mb-3">Lead Details</h5>

              <Row className="mb-3">
                <Col md={6}>
                  <small className="text-muted">Address</small>
                  <div>{details.address || "N/A"}</div>
                </Col>

                <Col md={3}>
                  <small className="text-muted">Status</small>
                  <div>{details.status || "N/A"}</div>
                </Col>

                <Col md={3}>
                  <small className="text-muted">Proposed Price</small>
                  <div>{details.proposed_price || "N/A"}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <small className="text-muted">Project Description</small>
                  <div>{details.project_description || "N/A"}</div>
                </Col>

                <Col md={6}>
                  <small className="text-muted">Lead Context</small>
                  <div>{details.context || "N/A"}</div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <small className="text-muted">Notes</small>
                  <div>{details.notes || "None"}</div>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-3">
                <button
                  className="lead-action-btn delete"
                  disabled={actionLoading === call.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel(call);
                  }}
                >
                  Cancel Call
                </button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) return <LoadingSpinner text="Loading scheduled calls..." />;

  return (
    <Container fluid className="page-container">
      {/* HEADER */}
      <div className="mb-4">
        <h1 className="page-header-title">Scheduled Calls</h1>
        <div className="page-header-subtitle">
          Click a row/card to view lead details.
        </div>
      </div>

      {/* SEARCH */}
      <div className="d-flex mb-4" style={{ maxWidth: "350px" }}>
        <Form.Control
          className="search-input"
          placeholder="Search name, phone, email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FaSearch className="text-muted ms-2" />
      </div>

      {/* DESKTOP TABLE */}
      <div className="d-none d-md-block table-responsive">
        <Table hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Countdown</th>
              <th>Phone</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 text-center text-muted">
                  No scheduled calls found.
                </td>
              </tr>
            ) : (
              filtered.map((call) => {
                const lead = call.lead;
                const countdown = getCountdown(call.scheduled_time);

                return (
                  <React.Fragment key={call.id}>
                    <tr
                      className={`lead-row-clickable ${
                        expandedId === call.id ? "table-active" : ""
                      }`}
                      onClick={() => toggleExpandRow(call)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="lead-avatar">
                            {lead.name.charAt(0)}
                          </div>
                          <div className="ms-2 fw-semibold">{lead.name}</div>
                        </div>
                      </td>

                      <td>
                        <Badge bg={countdown.variant}>{countdown.label}</Badge>
                      </td>

                      <td className="mono">{lead.phone}</td>

                      <td>
                        {expandedId === call.id ? (
                          <FaChevronUp className="chevron-icon" />
                        ) : (
                          <FaChevronDown className="chevron-icon" />
                        )}
                      </td>
                    </tr>

                    {expandedId === call.id && (
                      <tr>
                        <td colSpan={6}>{renderExpandedDetails(call)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* MOBILE CARD LIST */}
      <div className="d-md-none mobile-call-list">
        {filtered.length === 0 ? (
          <div className="text-center py-4 text-muted">
            No scheduled calls found.
          </div>
        ) : (
          filtered.map((call) => {
            const lead = call.lead;
            const countdown = getCountdown(call.scheduled_time);

            return (
              <div
                key={call.id}
                className="mobile-call-card"
                onClick={() => toggleExpandRow(call)}
              >
                <div className="mobile-call-header">
                  <div className="lead-avatar">
                    {lead.name.charAt(0)}
                  </div>

                  <div className="mobile-call-info">
                    <h5>{lead.name}</h5>
                    <p className="text-muted">{lead.phone}</p>
                  </div>

                  <Badge bg={countdown.variant}>{countdown.label}</Badge>
                </div>

                {expandedId === call.id && (
                  <div className="mobile-expanded-section">
                    {renderExpandedDetails(call)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Container>
  );
};

export default ScheduledCallsPage;
